import { useCallback, useEffect, useState } from 'react';
import { Drawer } from 'antd';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { closeInternalWorkspace } from '../../actions/internal_workspace_actions';
import InternalWorkspaceNotesTab from './InternalWorkspaceNotesTab';
import InternalWorkspaceAttachmentsTab from './InternalWorkspaceAttachmentsTab';

interface InternalWorkspaceDrawerProps extends RouteComponentProps {
	visible: boolean;
	activeTab: string;
	closeDrawer: () => void;
}

const InternalWorkspaceDrawer = ({
	visible,
	activeTab,
	closeDrawer,
	history,
	location,
}: InternalWorkspaceDrawerProps) => {
	const [currentTab, setCurrentTab] = useState(activeTab || 'notes');

	const onClose = useCallback(() => {
		closeDrawer();
		if (location.pathname.endsWith('/internalSpace')) {
			const workOrderIdMatch = location.pathname.match(
				/\/supplier\/workOrders\/detail\/([^/]+)\/internalSpace$/
			);
			if (workOrderIdMatch) {
				// Preserve `location.search` so `backlinkParams` survives the path swap.
				// SupplierWorkOrdersDetailPage re-derives the navbar back link from
				// the URL whenever the WO entity refreshes; dropping the search here
				// makes the next refresh reset the back link to a generic overview
				// path and breaks the "Back to Work Orders" button's expected target.
				history.replace({
					pathname: `/supplier/workOrders/detail/${workOrderIdMatch[1]}/details`,
					search: location.search,
				});
			}
		}
	}, [closeDrawer, history, location.pathname, location.search]);

	useEffect(() => {
		if (visible && activeTab) {
			setCurrentTab(activeTab);
		}
	}, [activeTab, visible]);

	const handleShowNotes = useCallback(() => {
		setCurrentTab('notes');
	}, []);

	const handleShowAttachments = useCallback(() => {
		setCurrentTab('attachments');
	}, []);

	return (
		<Drawer
			visible={visible}
			onClose={onClose}
			placement="right"
			width={585}
			title={null}
			closable={false}
			headerStyle={{ display: 'none' }}
			bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
		>
			{/* Teal Header + Tabs */}
			<div className="flex-shrink-0 bg-teal-1">
				<div className="px-5 pb-3 pt-4">
					<div className="flex items-start justify-between">
						<div>
							<div className="h5medium text-character-primary">Internal Workspace</div>
							<div className="bodyregular mt-1 text-character-secondary">
								Only visible to your internal technician team.
							</div>
						</div>
						<button
							onClick={onClose}
							className="mt-0.5 cursor-pointer border-0 bg-transparent p-0 text-lg text-character-primary"
						>
							&#10005;
						</button>
					</div>
				</div>
				<div className="flex h-[46px] w-full">
					<button
						className={
							'bodymedium flex-1 cursor-pointer border-0 border-b-2 border-solid bg-transparent py-3 ' +
							(currentTab === 'notes'
								? 'border-teal-3 text-teal-3'
								: 'border-transparent text-character-secondary')
						}
						onClick={handleShowNotes}
					>
						Internal Notes
					</button>
					<button
						className={
							'bodymedium flex-1 cursor-pointer border-0 border-b-2 border-solid bg-transparent py-3 ' +
							(currentTab === 'attachments'
								? 'border-teal-3 text-teal-3'
								: 'border-transparent text-character-secondary')
						}
						onClick={handleShowAttachments}
					>
						Attachments
					</button>
				</div>
			</div>

			{/* Tab Content */}
			{currentTab === 'notes' ? (
				<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden">
					<InternalWorkspaceNotesTab />
				</div>
			) : (
				<div className="flex-1 overflow-y-auto px-6 pt-5">
					<InternalWorkspaceAttachmentsTab />
				</div>
			)}
		</Drawer>
	);
};

const mapStateToProps = (state: any) => ({
	visible: state.internalWorkspace.visible,
	activeTab: state.internalWorkspace.activeTab,
});

const mapDispatchToProps = (dispatch: any) => ({
	closeDrawer: () => dispatch(closeInternalWorkspace()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InternalWorkspaceDrawer));
